import React, { useMemo, useState, useEffect } from 'react';
import { Bell, ChevronRight, Menu, User, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const Header = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { count: notificationCount, fetchCount } = useNotifications();
  const location = useLocation();
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [platformName, setPlatformName] = useState('Business Central');

  // Load profile photo and platform config
  useEffect(() => {
    const loadData = async () => {
      try {
        const [profileRes, configRes] = await Promise.all([
          api.get('/profile'),
          api.get('/configuracion/general'),
        ]);
        if (profileRes.data?.foto_url) {
          setProfilePhoto(profileRes.data.foto_url);
        }
        if (configRes.data?.platform_name) {
          setPlatformName(configRes.data.platform_name);
        }
      } catch (error) {
        console.error('Error loading header data:', error);
      }
    };
    
    if (user) {
      loadData();
    }
  }, [user]);

  // Polling para notificaciones cada 30 segundos
  useEffect(() => {
    if (user) {
      // Fetch inicial
      fetchCount();
      
      // Polling cada 30 segundos
      const interval = setInterval(fetchCount, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user, fetchCount]);

  const { breadcrumbs: dynamicBreadcrumbs } = useBreadcrumbs();

  // Sidebar guarda el árbol de menús en sessionStorage. Lo leemos y nos
  // suscribimos al evento 'menusUpdated' para mantener los breadcrumbs en
  // sincronía sin necesidad de recargar.
  const [menuTreeVersion, setMenuTreeVersion] = useState(0);
  useEffect(() => {
    const bump = () => setMenuTreeVersion((v) => v + 1);
    window.addEventListener('menusUpdated', bump);
    return () => window.removeEventListener('menusUpdated', bump);
  }, []);

  const menuTree = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('ibc_sidebar_menus_v3');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTreeVersion]);

  // Busca recursivamente la cadena de ancestros + nodo cuyo `path` coincide
  // exactamente con `pathname`, o cuyo `path` es prefijo de `pathname`.
  // Retorna `null` si no encuentra ningún match.
  const findPathInTree = (nodes, pathname) => {
    if (!nodes) return null;
    // 1) Coincidencia exacta primero (mejor precisión)
    const walk = (list, trail, matcher) => {
      for (const n of list) {
        const newTrail = [...trail, n];
        if (n.path && matcher(n.path)) return newTrail;
        if (n.children?.length) {
          const found = walk(n.children, newTrail, matcher);
          if (found) return found;
        }
      }
      return null;
    };
    const exact = walk(nodes, [], (p) => p === pathname);
    if (exact) return exact;
    // 2) Coincidencia por prefijo (más larga gana). Recolectamos todas y
    //    elegimos la cadena más profunda.
    let best = null;
    const collect = (list, trail) => {
      for (const n of list) {
        const newTrail = [...trail, n];
        if (n.path && (pathname === n.path || pathname.startsWith(n.path + '/'))) {
          if (!best || newTrail.length > best.length) best = newTrail;
        }
        if (n.children?.length) collect(n.children, newTrail);
      }
    };
    collect(nodes, []);
    return best;
  };

  const breadcrumbs = useMemo(() => {
    // 1) Dynamic breadcrumbs (plugins / hitos) toman precedencia
    if (dynamicBreadcrumbs?.length) {
      return dynamicBreadcrumbs.map(crumb => ({
        label: crumb.label,
        path: crumb.path === '#' ? null : crumb.path,
      }));
    }

    // 2) Derivar del árbol del sidebar (fuente de verdad para menús dinámicos
    //    de la BD, incluido el 3+ niveles).
    const chain = menuTree ? findPathInTree(menuTree, location.pathname) : null;
    if (chain && chain.length > 0) {
      const crumbs = chain.map((n) => ({
        label: n.label,
        // Grupos (sin path) no son clicables; la hoja activa apunta a sí misma
        path: n.path || null,
      }));
      // Si la URL excede el path del último nodo (e.g. detail /:id), añadimos
      // un crumb auto-etiquetado según el sub-segmento.
      const last = chain[chain.length - 1];
      if (last.path && location.pathname.length > last.path.length) {
        const tail = location.pathname.slice(last.path.length + 1);
        // Mapeo de sub-segmentos conocidos. Default 'Detalle'.
        const KNOWN_TAILS = {
          'nuevo': 'Nuevo',
          'nueva': 'Nuevo',
          'dashboard': 'Dashboard',
          'editar': 'Editar',
          'importar': 'Importar',
          'exportar': 'Exportar',
        };
        const firstSeg = tail.split('/')[0];
        const label = KNOWN_TAILS[firstSeg] || 'Detalle';
        crumbs.push({ label, path: null });
      }
      return crumbs;
    }

    // 3) Fallback legacy: mapa estático (solo cubre rutas que no están en la
    //    tabla `menus`, como /perfil, /notificaciones, etc.)
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbMap = {
      'dashboard': { label: 'Dashboard', parent: null },
      'usuarios': { label: 'Usuarios', parent: 'Administración' },
      'roles': { label: 'Roles', parent: 'Administración' },
      'menus': { label: 'Menús', parent: 'Administración' },
      'configuracion-general': { label: 'General', parent: 'Administración' },
      'auditoria': { label: 'Auditoría', parent: 'Administración' },
      'perfil': { label: 'Mi Perfil', parent: null },
      'notificaciones': { label: 'Notificaciones', parent: null },
      'plugins': { label: 'Plugins', parent: 'Administración' },
      'integraciones': { label: 'Integraciones', parent: 'Administración' },
      'parametros': { label: 'Parámetros', parent: 'Administración' },
      'hitos': { label: 'Hitos', parent: 'G. de Proyectos' },
      'proyectos': { label: 'Proyectos', parent: 'G. de Proyectos' },
    };

    if (pathSegments.length === 0) return [];

    const currentPage = pathSegments[pathSegments.length - 1];
    let pageInfo = breadcrumbMap[currentPage];

    if (!pageInfo && pathSegments.length > 1) {
      const parentPage = pathSegments[0];
      const parentInfo = breadcrumbMap[parentPage];
      if (parentInfo) {
        const crumbs = [];
        if (parentInfo.parent) {
          crumbs.push({ label: parentInfo.parent, path: null });
        }
        crumbs.push({ label: parentInfo.label, path: `/${parentPage}` });
        crumbs.push({
          label: currentPage === 'nueva' || currentPage === 'nuevo' ? 'Nuevo' : 'Configurar',
          path: location.pathname,
        });
        return crumbs;
      }
    }

    if (!pageInfo) return [];

    const crumbs = [];
    if (pageInfo.parent) {
      crumbs.push({ label: pageInfo.parent, path: null });
    }
    crumbs.push({ label: pageInfo.label, path: location.pathname });
    return crumbs;
  }, [location.pathname, dynamicBreadcrumbs, menuTree]);

  return (
    <header className="h-[54px] lg:h-[54px] skin-topbar flex items-center justify-between px-3 sm:px-4 lg:px-6" data-testid="header">
      <div className="flex items-center gap-2 lg:gap-4 min-w-0">
        {/* Mobile menu button */}
        <button
          onClick={onMenuToggle}
          className="lg:hidden p-2 hover:bg-white/40 rounded-md -ml-1"
          aria-label="Abrir menú"
        >
          <Menu size={22} className="skin-text-soft" />
        </button>

        {/* Title - hidden on small mobile */}
        <h2 className="hidden sm:block skin-topbar-title truncate">
          {platformName}
        </h2>
        
        {/* Mobile: Show current page */}
        <h2 className="sm:hidden skin-topbar-title truncate">
          {breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : 'Business Central'}
        </h2>
        
        {/* Breadcrumbs - hidden on mobile */}
        {breadcrumbs.length > 0 && (
          <div className="hidden md:flex items-center gap-2 skin-topbar-breadcrumb">
            <ChevronRight size={16} className="skin-text-mute" />
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <ChevronRight size={14} className="skin-text-mute" />}
                {crumb.path ? (
                  <Link 
                    to={crumb.path} 
                    className="font-medium"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="skin-text-mute">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 lg:gap-4">
        <Link 
          to="/notificaciones" 
          className="relative p-1.5 lg:p-2 hover:bg-white/40 rounded-md transition-colors skin-bell" 
          data-testid="notifications-button"
        >
          <Bell size={18} className="lg:w-5 lg:h-5" />
          {notificationCount > 0 && (
            <span className="skin-bell-dot absolute -top-0.5 -right-0.5 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </Link>
        
        {/* Profile avatar with dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 lg:gap-3 pl-1 pr-1 lg:pr-2 py-1 rounded-full hover:bg-[#F5F5F5] transition-colors focus:outline-none focus:ring-2 focus:ring-[#c5b8a2]/40"
              data-testid="user-menu-trigger"
              aria-label="Menú de usuario"
            >
              <div className="h-8 w-8 lg:h-10 lg:w-10 rounded-full overflow-hidden border-2 border-[#c5b8a2] shrink-0">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Foto de perfil"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="w-full h-full bg-[#c5b8a2] flex items-center justify-center text-white font-semibold text-sm lg:text-base"
                  style={{ display: profilePhoto ? 'none' : 'flex' }}
                >
                  {user?.nombre?.charAt(0) || <User size={18} />}
                </div>
              </div>
              <div className="hidden md:flex flex-col items-start leading-tight max-w-[180px]">
                <span
                  className="text-sm font-semibold text-[#2D2D2D] truncate"
                  data-testid="user-menu-nombre"
                >
                  {user?.nombre || '—'}
                </span>
                <span className="text-[10px] text-[#888888] truncate">
                  {user?.rol_nombre || ''}
                </span>
              </div>
              <ChevronDown
                size={14}
                className="hidden md:block text-[#888888] shrink-0"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-64"
            data-testid="user-menu-content"
          >
            <div className="px-3 py-2.5 border-b border-[#E8E8E7]">
              <div className="text-sm font-semibold text-[#2D2D2D] truncate">
                {user?.nombre || '—'}
              </div>
              <div className="text-xs text-[#5A5A5A] truncate" data-testid="user-menu-email">
                {user?.email || ''}
              </div>
              {user?.rol_nombre && (
                <div className="text-[10px] text-[#9d8272] mt-0.5 font-medium">
                  {user.rol_nombre}
                </div>
              )}
            </div>
            <DropdownMenuItem
              onClick={() => navigate('/perfil')}
              className="cursor-pointer gap-2"
              data-testid="user-menu-perfil"
            >
              <User size={14} className="text-[#5A5A5A]" />
              <span>Mi perfil</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="cursor-pointer gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid="user-menu-logout"
            >
              <LogOut size={14} />
              <span>Cerrar sesión</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
